import { motion } from "framer-motion";
import { Stars } from "@/components/ui/stars";
import { fadeIn, staggerContainer } from "@/lib/animations";

interface TestimonialProps {
  quote: string;
  name: string;
  title: string;
  imageSrc: string;
  delay: number;
}

const Testimonial = ({ quote, name, title, imageSrc, delay }: TestimonialProps) => (
  <motion.div
    variants={fadeIn("up", "tween", delay, 1)}
    className="relative bg-gray-800/90 backdrop-blur-md border border-gray-700 p-8 rounded-xl shadow-lg overflow-hidden group"
  >
    <div className="mb-6">
      <Stars />
    </div>
    <p className="text-gray-300 mb-6 font-light">{quote}</p>
    <div className="flex items-center">
      <img src={imageSrc} alt={name} className="w-12 h-12 rounded-full object-cover mr-4" />
      <div>
        <h4 className="font-light text-white">{name}</h4>
        <p className="text-violet-300 text-sm font-light">{title}</p>
      </div>
    </div>
    <div className="absolute top-0 right-0 -mt-4 -mr-4 h-20 w-20 bg-violet-600 opacity-0 group-hover:opacity-10 rounded-full transition-opacity duration-300"></div>
    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 h-16 w-16 bg-violet-600 opacity-0 group-hover:opacity-10 rounded-full transition-opacity duration-300"></div>
  </motion.div>
);

export function Testimonials() {
  const testimonials = [
    {
      quote: "Vettor's AI agents have completely transformed our recruiting workflow. Our team now spends 70% less time on administrative tasks, allowing us to focus on what really matters - building relationships with top candidates.",
      name: "Sarah Chen",
      title: "Head of Talent Acquisition, FinTech Startup",
      imageSrc: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&h=100&q=80"
    },
    {
      quote: "The AI-powered outreach and scheduling have been game changers. Our recruiters can focus on high-touch candidate experiences while Vettor's agents handle repetitive tasks. Interview coordination is now completely streamlined.",
      name: "Michael Rodriguez",
      title: "Director of Recruiting, Enterprise SaaS",
      imageSrc: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&h=100&q=80"
    },
    {
      quote: "Implementing Vettor's AI agent workflow reduced our time-to-hire by 45%. The AI sourcing assistant helps identify candidates we might have missed, and the automated screening process ensures we only spend time talking to qualified candidates.",
      name: "Priya Sharma",
      title: "Talent Partner, Tech Startup",
      imageSrc: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&h=100&q=80"
    },
  ];

  return (
    <section id="testimonials" className="py-24 bg-gray-900 overflow-hidden">
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <motion.div 
          variants={fadeIn("up", "tween", 0.2, 1)}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-light text-white">What Our Clients Say</h2>
          <p className="mt-4 text-xl text-gray-400 font-light">Talent teams at companies of all sizes trust Vettor's AI agents to transform their recruiting workflow</p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Testimonial
              key={index}
              quote={testimonial.quote}
              name={testimonial.name}
              title={testimonial.title}
              imageSrc={testimonial.imageSrc}
              delay={0.3 + index * 0.1}
            />
          ))}
        </div>
      </motion.div>
    </section>
  );
}
